/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import differenceBy from 'lodash.differenceby';
import isNil from 'lodash.isnil';
import isString from 'lodash.isstring';
import {resolvePermission} from '../entities/EntityUtils';
import {info} from '../logger/logger';
import {entityId} from '../services/ConnectUtils';
import {
  ApplicationFeature,
  Applications,
  EntityName,
  EntityOperation,
  GenericObject,
  Link,
  Permission,
  Role,
  RoleResult,
  Roles,
  User
} from '../types/entities';
import {ApplicationPage} from '../types/pages';
import {safeJSONParse} from '../utils/JSONUtils';
import {
  compactFeatures,
  getAllFeaturePermissions,
  getAllowedFeatures,
  getRoles,
  getUserPermissions,
  getUserRoles,
  hasApplication,
  isReadOnly,
  logFeatures,
  normalizePermissions,
  PermissionMap,
  throwAbstractException,
  verifyFeatures
} from './helpers';

export type InitializeParams = {
  user: User;
  roles: Role[];
  allFeatures: ApplicationFeature[];
  appKey: string;
  enableLogging?: boolean;
  applications?: Applications;
  isReadOnlyUser?: boolean;
  resolved?: boolean;
};

export type GetPermittedRoutesParams = {
  pages: ApplicationPage[];
  permitted?: ApplicationPage[];
  parent?: ApplicationFeature | any;
  isChild?: boolean;
};

export enum PermissionService {
  ICN = 'ICN',
  BLE = 'BLE',
  IVT = 'IVT',
  CARGO = 'CARGO',
  DOOR = 'DOOR',
  TPMS = 'TPMS',
  WETS = 'WETS',
  BMS = 'BMS',
  LMS = 'LMS',
  WMS = 'WMS',
  EA = 'EA'
};

export const icnPermissions = {
  first_notice_of_loss_data       : PermissionService.ICN,
  accident_reconstruction_data    : PermissionService.ICN,
  predictive_physical_damage_data : PermissionService.ICN,
  icnplus                         : PermissionService.ICN
};

export const services = {
  ion_tag_data    : PermissionService.BLE,
  ion_vision_data : PermissionService.IVT,
  cargosense_data : PermissionService.CARGO,
  doorstate_data  : PermissionService.DOOR,
  tpms_data       : PermissionService.TPMS,
  wets_data       : PermissionService.WETS,
  bms_data        : PermissionService.BMS,
  lms_data        : PermissionService.LMS,
  wms_data        : PermissionService.WMS
};

export const edgeAppService = {
  edge_App_data : PermissionService.EA
};

/**
 * Defines the ApplicationFeatures interface, implemented by the ApplicationFeatures
 * class and used for typing application specific Feature implementations.
 */
export interface IApplicationFeatures {
  initialize(params: InitializeParams): void
  isInitialized(): boolean
  hasFeature(...ids): boolean;
  has(...ids): boolean;
  hasAny(...ids): boolean;
  getPermittedFeatures(): GenericObject;
  getPermittedPages(params: GetPermittedRoutesParams): GenericObject
  canAccessPage(pages: ApplicationPage[], targetRoute: string): boolean;
  canAccessRole(role: Role): boolean;
  getPermittedRoles(roles: Role[]): Role[];
  getRoles(user: User): Role[];
  getUserRole(): RoleResult | Role;
  getFeatureName(id: string): string | undefined;
  getPreservedPermissions(features: ApplicationFeature, role: Role): Link[];
  getAllApplicationFeatures(): ApplicationFeature[];
  verifyApplication(featureId: string): boolean;
  getUserApplications(): Applications;
  __reset__(): void;
  hasPermission(entity: EntityName, operation: EntityOperation): boolean;
  getMappedPermissions(featureId: string): GenericObject;
  isDeviceFeature(featureId: string): boolean;
  isInternalFeature(featureId: string): boolean;
  isRestrictedFeature(featureId: string): boolean;
  isCalampRole(roles: Link[]): boolean;
  isInternalRole(): boolean;
  isVehicleFeature(featureId: string): boolean;
  isVehicleProfileFeature(featureId: string): boolean;
  isUserHasInternalVESRole(): boolean;
};

/**
 * The ApplicationFeatures API provides the abstraction for implementing a single,
 * authoritative point of entry for initializing, validating, and determining Application
 * Feature availability for authenticated Users based on the Features available to the
 * User's Application Roles.
 *
 * Each iOn Application which leverages the Application Features pattern should extend
 * this class and provide references to it's specific features. For in-depth examples,
 * see the accompanying tests and mocks.
 *
 * Feature Ids should be defined within the features.json document for each specific
 * application, the cactc-ui-seed project can be used as a reference:
 *
 * [Seed Script](http://bitbucket.pd.calampcorp.com:7990/projects/CACTC/repos/cactc-ui-seed/browse) |
 * [Feature Definitions](http://bitbucket.pd.calampcorp.com:7990/repos/cactc-ui-seed/browse/src/json/features.json)
 *
 *
 * ```typescript
 *
 *   import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
 *
 *   // implement an application specific features api ...
 *   class Features extends ApplicationFeatures {
 *     constructor() {
 *       super();
 *       this._applications = {
 *         [ADD_ONS_CRASHBOXX] : CRASHBOXX_APP_KEY
 *       };
 *      }
 *
 *     initialize(params: InitializeParams) {
 *       super.initialize(params);
 *     }
 *   };
 *   export default new Features();
 *
 * ```
 *
 * ```typescript
 *
 *   import Features from '@/features';
 *
 *   [LOAD_ROLES_RESPONSE]: (draft, {roles: Role[]}) => {
 *     Features.initialize({
 *       user        : draft.user,
 *       allFeatures : draft.features,
 *       appKey      : get('appKey'),
 *       roles
 *     });
 *     ...
 *   }
 *
 * ```
 */
class ApplicationFeatures implements IApplicationFeatures {
  /**
   * When true, indicates that the Features API has been initialized and is
   * ready for use within the application. This property is set to true upon
   * successfully completing the initialization process via the initialize
   * method.
   */
  _initialized = false;

  /**
   * Defines a reference to the authenticated User from which the Features API
   * has been initialized.
   */
  _user: User;

  /**
   * Defines a reference to the application key against which all features
   * are validated and defined.
   */
  _appKey: string;

  /**
   * Defines a reference to the application Roles associated with the given
   * appKey.
   */
  _appRoles: Role[];

  /**
   * Defines a reference to the application permissions associated with the given
   * user.
   */
  _isReadOnlyUser: boolean;

  /**
   * Defines a reference to the application Roles associated with the given
   * user.
   */
  _userRoles: RoleResult[] | Role[];

  /**
   * Defines a map of all permissions assigned to the authenticated user's
   * application roles.
   */
  _userPermissions: PermissionMap;

  /**
   * Defines a reference to the application role features associated the User
   * has access to
   */
  _features: ApplicationFeature[];

  /**
   * Defines a reference to all application features defined for the application.
   */
  _allFeatures: ApplicationFeature[];

  /**
  * Defines a reference to all application features defined for the application as a flattened list.
  */
  _allFeaturesList: ApplicationFeature[];

  /**
   * Defines a reference to all permission available defined for the application.
   */
  _allPermissions: Permission[] | Link[];

  /**
   * Provides an optional mapping of additional application keys to their respective
   * features.
   */
  _applications: GenericObject = {};

  /**
   * Provides a reference to all Application Features available to the authenticated
   * user based on the Features assigned to their Role(s).
   *
   * The PERMITTED_FEATURES object will have a key added for each specific Feature
   * assigned to their Application Role, whereas the key is set to the value of a
   * pre-defined feature id. This, in turn, is used to determine the features the
   * User has access  to. For example, if a User's Role has the ADMIN_ALERTS_PAGE
   * Feature, PERMITTED_FEATURES will have the ADMIN_ALERTS_PAGE id added as a key.
   *
   * @private
   */
  _permittedFeature = {};

  /**
   * Provides a reference to all Services available to the authenticated
   * user to modify based on the Permissions assigned to their Role(s).
   */
  _serviceFeatures = {};

  /**
   * The initialize function provides the single point of entry into Application
   * Application Features in the context of the UI for a an authenticate User's
   * Role Features.
   *
   * Upon successful authentication, the initialize method is to be invoked with
   * the authenticated User entity (object), and Roles List. Each Role is then
   * inspected for Application Features (via the Role's extended attributes),
   * and subsequently used to determine which features the User has access to.
   *
   * ```typescript
   *
   *   import Features from '@/security';
   *
   *   [LOAD_ROLES_RESPONSE]: (draft, {roles: Role[]}) => {
   *     Features.initialize({
   *       user        : draft.user,
   *       allFeatures : draft.features,
   *       appKey      : get('appKey'),
   *       roles
   *     });
   *     ...
   *   }
   *
   * ```
   *
   * @method initialize
   * @param  {object} user The authenticated user entity retrieved
   *         from CTC services.
   * @param  {array} roles The complete representation of Roles assigned
   *         to the authenticated user as retrieved from CTC services.
   * @param  {array} allFeatures The complete list of Application
   *         application features.
   * @param  {string} appKey The application key applicable to the
   *         Roles of interest.
   * @param  resolved When `true`, indicates that user roles have been resolved
   *         and should be used directly. This implies that the roles provided
   *         within the initialize parameter have b
   */
  initialize({
    user,
    roles,
    allFeatures,
    appKey,
    enableLogging,
    resolved
  }: InitializeParams) {
    let appRoles;
    let userRoles;
    if (!resolved) {
      appRoles  = getRoles(roles, appKey);
      userRoles = getUserRoles(user, appRoles, appKey);
    } else {
      appRoles  = roles;
      userRoles = roles;
    }

    this._allFeaturesList = this.createApplicationFeaturesList(safeJSONParse(JSON.stringify(allFeatures)));
    const features        = getAllowedFeatures(userRoles, this.getAllApplicationFeaturesList());
    const verified        = verifyFeatures(features, user, this._applications);
    const compact         = compactFeatures(verified);
    const allPermissions  = getAllFeaturePermissions(allFeatures);
    this._user            = user;
    this._appKey          = appKey;
    this._appRoles        = appRoles;
    this._userRoles       = userRoles;
    this._features        = verified;
    this._allFeatures     = allFeatures;
    this._allPermissions  = allPermissions;
    this._isReadOnlyUser  = isReadOnly(userRoles || []);
    enableLogging && logFeatures(verified);
    Object.keys(compact).forEach(id => {
      this._permittedFeature[id] = true;
    });
    this._initialized = true;
    this.verifyServiceBasedPermissions(userRoles);
    if (process?.env?.NODE_ENV !== 'test') {
      info('iOn Security / Feature API successfully initialized');
    }
  }

  /**
   * Allows for determining if the Features API has been initialized via a call
   * to the initialize method.
   *
   * @method  isInitialized
   * @returns {boolean} True if the Features API has been initialized via a
   *                    call to the initialize method, otherwise, false.
   */
  isInitialized(): boolean {
    return this._initialized === true;
  };

  /**
   * Allows for determining if value of _isReadOnlyUser is true or false based on the role permission.
   *
   * ```typescript
   *   // determine if the user has read only permissions
   *   import Features from 'security';
   *
   *   // if user has access to the alerts page ...
   *   if (Features.isReadOnlyUser()) {
   *     ...
   *   }
   *
   * ```
   * @method  isReadOnlyUser
   * @returns {boolean} True if the verifyUserRole method returns true, false otherwise.
   */
  isReadOnlyUser(): boolean {
    return this._isReadOnlyUser;
  }

  /**
   * The verifyServiceBasedPermissions function assigns true/false to the services
   * based on the authenticated user's permissions.
   *
   * ```typescript
   *   import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
   *
   *   class SomeApplicationFeatures extends ApplicationFeatures {
   *
   *     public verifyServiceBasedPermissions(roles: Roles) {
   *       super.verifyServiceBasedPermissions(roles);
   *     }
   *   }
   * ```
   */
  protected verifyServiceBasedPermissions(roles: Roles) {
    const userPermissions    = roles[0]?.permissions;
    const servicePermissions = Object.keys({...services, ...icnPermissions, ...edgeAppService});
    const edgeApp            = Object.keys(edgeAppService)[0];

    const userServicePermissions = servicePermissions.filter((item) =>
      userPermissions?.filter(({title}) => title.includes(item)).length > 0);

    if (userServicePermissions?.length > 0) {
      const otherServicePermissions = Object.keys(services);

      otherServicePermissions.forEach(item => {
        if (userServicePermissions.indexOf(item) > -1) {
          this._serviceFeatures[services[item]] = true;
        } else {
          this._serviceFeatures[services[item]] = false;
        }
      });

      const icnServicePermissions = Object.keys(icnPermissions);

      if (icnServicePermissions.some(item => userServicePermissions.indexOf(item) > -1)) {
        this._serviceFeatures[PermissionService.ICN] = true;
      } else {
        this._serviceFeatures[PermissionService.ICN] = false;
      }

      this._serviceFeatures[PermissionService.EA] = servicePermissions.indexOf(edgeApp) > -1;
    }
  };

  /**
   * The hasFeature function provides the main API into the Application security
   * module, and allows for determining if the user has access to a given CalAmp
   * iOn Feature.
   *
   * Access to specific features is based on the  Application Features available
   * to the Roles to which the User has been assigned.
   *
   * ```typescript
   *
   *   // determine if the user has access to a specific feature ...
   *   import Features, {ALERTS_PAGE, REPORTS_PAGE} from 'security';
   *
   *   // if user has access to the alerts page ...
   *   if (Features.hasFeature(ALERTS_PAGE)) {
   *     ...
   *   }
   *
   *   // if user has access to both the alerts and reports pages...
   *   if (Features.hasFeature(ALERTS_PAGE, REPORTS_PAGE)) {
   *     ...
   *   }
   *
   * ```
   *
   * @method  hasFeature
   * @param   {string|array} id An id, or multiple ids, of a pre-defined
   *          Feature which has been exported as a constant from the
   *          Features module; e.g. DASHBOARD_PAGE, ADMIN_ASSETS_PAGE,
   *          etc.
   * @returns {boolean} True if the user has access to the requested
   *          Feature (DASHBOARD_PAGE), otherwise, false.
   */
  hasFeature(...ids): boolean {
    let permitted = true;
    ids.forEach(id => {
      if (this._permittedFeature[id] !== true) {
        permitted = false;
      }
    });
    return permitted;
  };

  /**
   * Provides a short-hand alias to the hasFeature method simply for
   * convenience.
   *
   * @see hasFeature
   * @method has
   * @param   {string|array} id An id, or multiple ids, of a pre-defined
   *          Feature which has been exported as a constant from the
   *          Features module; e.g. DASHBOARD_PAGE, ADMIN_ASSETS_PAGE,
   *          etc.
   * @returns {boolean} True if the user has access to the requested
   *          Feature (DASHBOARD_PAGE), otherwise, false.
   */
  has = (...ids): boolean => this.hasFeature(...ids);

  /**
   * The hasAny method provides a convenience for determining if any of
   * the provided Features are permitted to the User via their assigned
   * Application Roles.
   *
   * ```typescript
   *
   *   // determine if the user has access to a specific feature ...
   *   import Features, {
   *     ADMIN,
   *     ADMIN_ASSETS_PAGE,
   *     ADMIN_OPERATORS_PAGE
   *     ADMIN_ROUTES_PAGE,
   *     ADMIN_USERS_PAGE,
   *     ADMIN_ROLES_PAGE
   *     ADMIN_GROUPS_PAGE,
   *     ADMIN_ALERTS_PAGE,
   *     ADMIN_IO_PROFILES_PAGE,
   *     ADMIN_LANDMARK_GEOFENCE_PAGE,
   *     ADMIN_MAP_FEATURES
   *   } from 'security';
   *
   *   // if user has any admin featues ...
   *   if (Features.hasAny(
   *     ADMIN,
   *     ADMIN_ASSETS_PAGE,
   *     ADMIN_OPERATORS_PAGE
   *     ADMIN_ROUTES_PAGE,
   *     ADMIN_USERS_PAGE,
   *     ADMIN_ROLES_PAGE
   *     ADMIN_GROUPS_PAGE,
   *     ADMIN_ALERTS_PAGE,
   *     ADMIN_IO_PROFILES_PAGE,
   *     ADMIN_LANDMARK_GEOFENCE_PAGE,
   *     ADMIN_MAP_FEATURES
   *   )) {
   *     ...
   *   }
   *
   * ```
   *
   * @method  hasAny
   * @param   {string|array} id An id, or multiple ids, of pre-defined
   *          Features which have been exported as constants from the
   *          Features module; e.g. DASHBOARD_PAGE, ADMIN_ASSETS_PAGE,
   *          etc. This method will return true if the User has any of
   *          the Features passed as arguments to this method.
   * @returns {boolean} True if the User has any of the provided Features,
   *          otherwise, false.
   */
  hasAny(...ids): boolean {
    return Boolean(ids.find(id => this._permittedFeature[id] === true));
  };

  /**
   * The `hasPermission` method allows for determining if the authenticated
   * user has a given CTC permission assigned to their authenticated application
   * `Role` based on the provided `Entity` (resource) and `Operation`.
   *
   * ```typescript
   *  import {EntityName, EntityOperation} from '@ion/core/src/types/entities';
   *  import Features from '@/security/Features';
   *  ...
   *
   *  // entity and operation string literal arguments ...
   *  const hasAccountDelete = Features.hasPermission('account', 'delete');
   *
   *  // or, EntityName and EntityOperation type arguments ...
   *  const hasAccountDelete = Features.hasPermission(
   *    EntityName.account,
   *    EntityOperation.Delete
   * );
   *
   *  // or, any combination of the two ...
   *  const hasAccountDelete = Features.hasPermission(
   *   'account', EntityOperation.Delete
   *  );
   *  const hasAccountDelete = Features.hasPermission(
   *   EntityName.account, 'delete'
   *  );
   *
   *  // permissions will also be resolved regardless of case ...
   *  const hasAccountDelete = Features.hasPermission('Account', 'DELETE');
   * ```
   *
   * @param   entity An `Entity` enum value or the string literal entity name.
   * @param   operation An `Operation` enum value or the string literal operation.
   * @returns `true` if the user has the specified permission, otherwise, `false`.
   */
  hasPermission(entity: EntityName, operation: EntityOperation): boolean {
    if (!this._userPermissions || !this._userPermissions?.size) {
      this._userPermissions = getUserPermissions(this._userRoles as Role[]);
    }
    return !!this._userPermissions.get(resolvePermission(entity, operation));
  }

  /**
   * The getPermittedFeatures function returns an object which contains
   * the ids of all features the user has access to based on the Features
   * available to to their assigned Application Role(s).
   *
   * ```typescript
   *
   *   import {getPermittedFeatures} from 'security';
   *
   *   // retrieve all feature ids to which the user has access ...
   *   const permitted = getPermittedFeatures();
   *
   * ```
   *
   *
   * @method  getPermittedFeatures
   * @returns {object} An object containing the ids of each Application
   *          Feature the User has access to based on the features available
   *          to their assigned Application Role(s).
   */
  getPermittedFeatures(): GenericObject {
    return this._features.map((feature: ApplicationFeature) => Object.assign({}, feature));
  };

  /**
   * Given a list of all Application Routes which have been defined within
   * the nav.js reducers state and contain Feature Ids, recursively filters
   * the list of pages and returns only the pages which the User has access
   * to based on their assigned Role Features.
   *
   * ```typescript
   *
   *  import Features from '@/security';
   *
   *  const pages = [{
   *    id            : DASHBOARD_PAGE,
   *    route         : Route.DASHBOARD,
   *    isActive      : false,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    isPreview     : false
   *  }, {
   *    id            : TRACKING_PAGE,
   *    route         : Route.TRACKING,
   *    isActive      : false,
   *    isAuthorized  : false,
   *    showSideSheet : false,
   *    isPreview     : false
   *  }, {
   *    id            : ADMIN,
   *    route         : Route.ADMIN,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    isPreview     : true,
   *    pages         : [{
   *      id            : ADMIN_ASSETS_PAGE,
   *      route         : Route.ADMIN_ASSETS,
   *      isActive      : false,
   *    showSideSheet : false,
   *      showSideSheet : false,
   *      isPreview     : false
   *    }, ...]
   *  }];
   *
   * ```
   *
   *  // filter the above pages to only include those which are
   *  // available to the User via their assigned Application
   *  // Role Features ...
   *  const permittedRoutes = Features.getPermittedPages(pages);
   *
   * @method  filterRoutes
   * @param   {array} pages An array of all possible Application
   *          Application Routes as defined within the nav.js reducers
   *          state. Each route must contain an id which references a
   *          pre-defined Features Feature Id; for example DASHBOARD,
   *          ADMIN_ASSETS, ADMIN_ROUTES, etc.
   * @returns {array} An Array containing the provided Routes which
   *          the User has access to based on the Features available
   *          to the User via their assigned Application Roles.
   */
  getPermittedPages({
    pages,
    permitted = [],
    isChild = false,
    parent = null
  }: GetPermittedRoutesParams) {
    const allowedChildren = parent ? [] : null;
    pages.forEach((page: ApplicationPage) => {
      const canAccess = this.hasFeature(page.id);
      const isChildPageAuthorized = page.pages?.some(({id}) => this.hasFeature(id));
      if (canAccess || isChildPageAuthorized) {
        if (!isChild) {
          const {pages} = page;
          if (pages?.length) {
            this.getPermittedPages({
              parent  : page,
              isChild : true,
              permitted,
              pages
            });
          }
          permitted.push({
            ...page,
            isAuthorized : true,
            name         : this.getFeatureName(page.id)
          });
        } else if (parent) {
          allowedChildren.push(page);
        }
      }
    });
    if (parent) {
      parent.pages = allowedChildren.map((page: ApplicationPage) => ({...page, isAuthorized: true}));
    }
    return permitted;
  };

  /**
   * Given a list of all Application Feature Routes which have been defined
   * within the nav.js reducers state, recursively searches for the given
   * route and determines if the User is permitted access to the provided
   * route.
   *
   * ```typescript
   *
   *  import Features from '@/security';
   *
   *  const pages = [{
   *    id            : DASHBOARD_PAGE,
   *    route         : Route.DASHBOARD,
   *    isActive      : false,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    isPreview     : false
   *  }, {
   *    id            : TRACKING_PAGE,
   *    route         : Route.TRACKING,
   *    isActive      : false,
   *    isAuthorized  : false,
   *    showSideSheet : false,
   *    isPreview     : false
   *  }, {
   *    id            : ADMIN,
   *    route         : Route.ADMIN,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    showSideSheet : false,
   *    isPreview     : true,
   *    pages         : [{
   *      id            : ADMIN_ASSETS_PAGE,
   *      route         : Route.ADMIN_ASSETS,
   *      isActive      : false,
   *      showSideSheet : false,
   *      showSideSheet : false,
   *      isPreview     : false
   *    }, ...]
   *  }];
   *
   * ```
   *
   *  // determine if the user has access to the tracking page route ...
   *  const permittedRoutes = Features.canAccessPage(pages, Route.TRACKING);
   *
   * @method  canAccessPage
   * @param   {array} routes An array of all Application Feature Routes
   *          as defined within the nav.js reducers state.
   * @param   {string} targetRoute The route to determine if the User has
   *          access to.
   * @returns {boolean} True if the User has access to the specified route,
   *          otherwise, false.
   */
  canAccessPage(pages: ApplicationPage[], targetRoute: string): boolean {
    let permitted = false;
    const hasPage = (pages: ApplicationPage[]) => {
      pages.forEach((page: ApplicationPage) => {
        const {id, route, pages: subpages} = page;
        if (route === targetRoute) {
          permitted = this.hasFeature(id);
        } else if (subpages?.length) {
          hasPage(subpages);
        }
      });
    };
    hasPage(pages);
    return permitted;
  }

  /**
   * Given an Application Role, determines if the Role contains Features
   * which the authenticated User's Roles does not have access to, in which
   * case, returns false (as a User can not access a Role higher than that
   * which has been assigned to them). If the given Role contains a subset
   * of the Features assigned to the authenticated Users Role, or the exact
   * same features, then returns true.
   *
   * @static
   * @method  canAccessRole
   * @param   {Role} role the role which is to be verified.
   * @param   {boolean} verifyAssigned When true, the users roles will be
   *          inspected to determine if they are assigned the given role,
   *          in which case they can not access the role, and this method
   *          will return false.
   * @returns {boolean} false if the Role contains Features which the User
   *          does not have access to, otherwise, true.
   */
  canAccessRole(role: Role, verifyAssigned = false): boolean {
    const isAssigned = verifyAssigned ? Boolean(this._user?.roles?.find((item: Link) => {
      return entityId(item) === role.id;
    })) : false;
    if (!isAssigned) {
      const features = compactFeatures(getAllowedFeatures([role], this.getAllApplicationFeaturesList()));
      let allowed = true;
      Object.keys(features).forEach(id => {
        if (!this.hasFeature(id)) {
          allowed = false;
        }
      });
      return allowed;
    }
    return false;
  }

  /**
   * The getPermittedRoles method provides a convenience which allows for
   * filtering a list of Roles to only those which are contain the same
   * Features or a sub-set of the Features available to the authenticated
   * User's Roles. Roles contained in the list which contain any Features
   * which the User's Roles do not have access to are omitted.
   *
   * @method  getPermittedRoles
   * @param   {array} roles An array of Roles against which the User's Roles
   *          are to be inspected. Only Roles which do not contain Features
   *          which are not available to the authenticated Users Role will
   *          be returned.
   * @returns {array} An array of Roles which contains the same permissions
   *          or a sub-set of the permissions available to the authenticated
   *          User's Roles.
   */
  getPermittedRoles(roles: Role[]): Role[] {
    const permitted = [];
    roles.forEach(role => {
      if (this.canAccessRole(role)) {
        permitted.push(role);
      }
    });
    return permitted;
  }

  /**
   * Provides a convenience for retrieving the fully qualified representation
   * of all Application Roles for the given User.
   *
   * @method  getRoles
   * @param   {object} user The User entity for which all Application Roles
   *          are to be returned. If not provided, the authenticated User
   *          will be used as the subject of the operation.
   * @returns {array} An Array of Application Roles in their corresponding
   *          complete entity representations.
   */
  getRoles(user: User): Role[] {
    const subject = user || this._user;
    const roles   = getUserRoles(subject, this._appRoles, this._appKey);
    const matches = {};
    subject.roles.forEach(role => {
      matches[role.href] = true;
    });
    return roles.filter(role => matches[role._self.href]);
  }

  /**
   * Provides a convenience method which returns the Application Role
   * which has been assigned to the authenticated User.
   *
   * @method  getUserRole
   * @returns {object} The Application Role assigned to the authenticated
   *          User.
   */
  getUserRole(): RoleResult | Role {
    const {_userRoles:roles} = this;
    return roles?.length ? roles[0] : null;
  }

  /**
   * The getFeatureName method provides a convenience for retrieving the
   * name of a given Feature based on the Feature's Id. If the Feature is
   * not permitted to the authenticated User's Roles, undefined will be
   * returned.
   *
   * ```typescript
   *   import Features from '@/security';
   *
   *   // get the name of a feature based on the features id ...
   *   const name = Features.getFeatureName(Features.DASHBOARD_PAGE);
   *
   * ```
   *
   * @static
   * @method  getFeatureName
   * @param   {string} id The id of the feature for which the corresponding
   *          Feature name is to be returned.
   * @returns {string} The name of the Feature for the given Feature id.
   */
  getFeatureName(id: string): string | undefined {
    const feature: ApplicationFeature = this._features.find((feature: ApplicationFeature) => {
      return feature.id === id;
    });
    if (feature) {
      return feature.name;
    }
  }

  /**
   * The getPreservedPermissions method provides a means of retrieving all
   * CTC Permissions for the given features, while also preserving existing
   * permissions which the role contains which are not defined within any
   * of the Feature permissions.
   *
   * The process of preserving non-feature related permissions is as follows:
   *
   * <ul>
   *   <li>
   *     Obtain the diff between all pre-defined Application Feature Permissions
   *     and the existing Role's Permissions.
   *   </li>
   *   <li>
   *     Combine the updated Feature Permissions with the non-feature based
   *     CTC Permissions and return the result.
   *   </li>
   * <ul>
   *
   * ```typescript
   *   import Features from '@/security';
   *
   *   const permissions = Features.getPreservedPermissions(features, role);
   *
   * ```
   *
   * @method  getPreservedPermissions
   * @param   {object} features The features upon which the permissions are
   *          to be based.
   * @param   {object} role The existing Role for which non-feature specific
   *          permissions are to be preserved.
   * @returns {array} An array containing all feature permissions, as well as
   *          any additional permissions contained by the role which are not
   *          feature related.
   */
  getPreservedPermissions(features: ApplicationFeature, role: Role): Link[] {
    const unrelated   = differenceBy(role.permissions, this._allPermissions, 'href');
    const permissions = features.permissions.concat(unrelated);
    return normalizePermissions(permissions);
  }

  /**
   * Returns a deep copy of all features associated with the application. These features
   * are provided via the initialize method during Feature initialization.
   *
   * ```typescript
   *   import Features from '@/security';
   *
   *   const all: ApplicationFeature[] = Features.getAllApplicationFeatures();
   *
   * ```
   *
   * @method  getPreservedPermissions
   * @returns {ApplicationFeature[]} An array containing all features defined by the
   *          application.
   */
  getAllApplicationFeatures(): ApplicationFeature[] {
    return safeJSONParse(JSON.stringify(this._allFeatures)) as any;
  }

  /**
   * Tranverses the nested list of application features and converts into flattened list
   *
   * ```typescript
   *   import Features from '@/security';
   *
   *   const all: ApplicationFeature[] = Features.createApplicationFeaturesList();
   *
   * ```
   *
   * @method  createApplicationFeaturesList
   * @returns {ApplicationFeature[]} An array containing all features defined by the
   *          application.
   */
  createApplicationFeaturesList(appFeatures: ApplicationFeature[], featuresObject = []): ApplicationFeature[] {
    appFeatures?.forEach(({id, name, description, default: defValue, features, permissions}: ApplicationFeature) => {
      featuresObject.push({id, name, description, default: defValue, permissions});
      if (features?.length > 0) {
        this.createApplicationFeaturesList(features, featuresObject);
      }
    });
    return featuresObject;
  }

  /**
   * Returns a deep copy of flatened list of all features associated with the application. These features
   * are provided via the initialize method during Feature initialization.
   */
  getAllApplicationFeaturesList(): ApplicationFeature[] {
    return safeJSONParse(JSON.stringify(this._allFeaturesList)) as any;
  }

  /**
   * Determines if the authenticated user has access to the given feature and it's required
   * applications (if any).
   *
   * @method  verifyApplication
   * @param   {string} The feature id which is to be inspected for access to both the feature
   *          and it's required applications (if any).
   * @returns True if the user has access to the feature and, if applicable, it's required
   *          applications.
   */
  verifyApplication(featureId: string): boolean {
    const required = this._applications[featureId];
    return required ? hasApplication(this._user, required) : true;
  }

  /**
   * Provides a convenience method which returns the Applications
   * which has been subscribed by the authenticated User.
   *
   * @method  getUserApplications
   * @returns {object} The list of Applications subscribed by the authenticated
   *          User.
   */
  getUserApplications(): Applications {
    const {applications} = this._user;
    return applications?.length ? applications : null;
  }

  /**
   * Provides a convenience method which returnsthe Applications feature
   * for the passed application. Since iOn-CTC-Admin is allowing to create Role for other applications.
   * Here we are checking the features key is exist for the selected application.
   * if exist then return the parsed featuers else return as null.
   * @static
   * @method  getFeatures
   * @returns {object} The features object will be returned
   */
  getFeatures(application: GenericObject): GenericObject[] | undefined {
    let features: GenericObject[];
    if (!isNil(application.extendedAttribute)) {
      try {
        features = safeJSONParse(application.extendedAttribute.value)?.features;
      } catch (e) {
      }
    }
    return features;
  };

  /**
   * Provide a method to append roles from feature flag onto the existing features.
   *
   * ```typescript
   * Feature.updateRoleFeatures(['guid-1', 'guid-2']) // this_permission = {..., [guid-1]: true, [guid-2]: true}
   *  ```
   */
  updateRoleFeatures(features: string[]) {
    const newFeatures = features
      .filter(ApplicationFeatures.isFeatureId)
      .reduce((acc, feature) => {
        acc[feature] = true;
        return acc;
      }, {});
    this._permittedFeature = {
      ...this._permittedFeature,
      ...newFeatures
    };
  }

  /**
   * Provide utility method to check if a feature id is in expected format.
   *
   * ```typescript
   * import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
   *
   * ApplicationFeatures.isFeature('test') //false
   * ApplicationFeatures.isFeature('61409576-0c06-b170-ac8a-75bbc4b84c59') //true
   */
  public static isFeatureId(feature) {
    return isString(feature) &&
      /^([a-z0-9]{8}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{12})$/.test(feature);
  }

  /**
   * Allows for determining if user has permissions to access provided service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *   // if user has access to the provided service ...
   *   if (Features.hasService(PermissionService.ICN)) {
   *     ...
   *   }
   *
   * ```
   * @method  hasService
   * @returns {boolean} True if the user has permission to access provided service, false otherwise.
   */
  public hasService(service: PermissionService): boolean {
    return this._serviceFeatures[service];
  }

  /**
   * Allows for determining if user has permissions to access ICN service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableICN = Features.hasICN();
   *
   * ```
   * @method  hasICN
   * @returns {boolean} True if the user has permission to access ICN service, false otherwise.
   */
  public hasICN(): boolean {
    return this._serviceFeatures[PermissionService.ICN];
  }

  /**
   * Allows for determining if user has permissions to access BLE service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableBLE = Features.hasBLE();
   *
   * ```
   * @method  hasBLE
   * @returns {boolean} True if the user has permission to access BLE service, false otherwise.
   */
  public hasBLE(): boolean {
    return this._serviceFeatures[PermissionService.BLE];
  }

  /**
   * Allows for determining if user has permissions to access IVT service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableIVT = Features.hasIVT();
   *
   * ```
   * @method  hasIVT
   * @returns {boolean} True if the user has permission to access IVT service, false otherwise.
   */
  public hasIVT(): boolean {
    return this._serviceFeatures[PermissionService.IVT];
  }

  /**
   * Allows for determining if user has permissions to access CARGO service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableCARGO = Features.hasCARGO();
   *
   * ```
   * @method  hasCARGO
   * @returns {boolean} True if the user has permission to access CARGO service, false otherwise.
   */
  public hasCARGO(): boolean {
    return this._serviceFeatures[PermissionService.CARGO];
  }

  /**
   * Allows for determining if user has permissions to access DOOR service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableDOOR = Features.hasDOOR();
   *
   * ```
   * @method  hasDOOR
   * @returns {boolean} True if the user has permission to access DOOR service, false otherwise.
   */
  public hasDOOR(): boolean {
    return this._serviceFeatures[PermissionService.DOOR];
  }

  /**
   * Allows for determining if user has permissions to access TPMS service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableTPMS = Features.hasTPMS();
   *
   * ```
   * @method  hasTPMS
   * @returns {boolean} True if the user has permission to access TPMS service, false otherwise.
   */
  public hasTPMS(): boolean {
    return this._serviceFeatures[PermissionService.TPMS];
  }

  /**
   * Allows for determining if user has permissions to access WETS service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableWETS = Features.hasWETS();
   *
   * ```
   * @method  hasWETS
   * @returns {boolean} True if the user has permission to access WETS service, false otherwise.
   */
  public hasWETS(): boolean {
    return this._serviceFeatures[PermissionService.WETS];
  }

  /**
   * Allows for determining if user has permissions to access BMS service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableBMS = Features.hasBMS();
   *
   * ```
   * @method  hasBMS
   * @returns {boolean} True if the user has permission to access BMS service, false otherwise.
   */
  public hasBMS(): boolean {
    return this._serviceFeatures[PermissionService.BMS];
  }

  /**
   * Allows for determining if user has permissions to access LMS service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableLMS = Features.hasLMS();
   *
   * ```
   * @method  hasLMS
   * @returns {boolean} True if the user has permission to access LMS service, false otherwise.
   */
  public hasLMS(): boolean {
    return this._serviceFeatures[PermissionService.LMS];
  }

  /**
   * Allows for determining if user has permissions to access WMS service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableWMS = hasWMS();
   *
   * ```
   * @method  hasWMS
   * @returns {boolean} True if the user has permission to access WMS service, false otherwise.
   */
  public hasWMS(): boolean {
    return this._serviceFeatures[PermissionService.WMS];
  }

  /**
   * Allows for determining if user has permissions to access EA service true or false
   * based on the role permission of authenticated user.
   *
   * ```typescript
   *   import Features from 'security';
   *
   *  const enableEA = hasEA();
   *
   * ```
   * @method  hasEA
   * @returns {boolean} True if the user has permission to access EA service, false otherwise.
   */
  public hasEA(): boolean {
    return this._serviceFeatures[PermissionService.EA];
  }

  /**
   * Determines if the current user is considered an 'Internal Role', or 'Calamp Role'.
   *
   * Used to determine the visibility of components or allowance of extra data fetches.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isInternalRole();
   *
   * ```
   *
   * @returns whether the currently logged in user is considered an internal role.
   */
  isInternalRole(): boolean {
    return throwAbstractException('isInternalRole');
  }

  /**
   * @deprecated opt instead for isInternalRole
   *
   * Determines if the current user is considered an 'Internal Role', or 'Calamp Role'.
   *
   * Used to determine the visibility of components or allowance of extra data fetches.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const {roles} = useAuthenticatedUser();
   *   const showFeature = Features.isCalampRole(roles);
   *
   * ```
   *
   * @returns whether the currently logged in user is considered a calamp role.
   */
  isCalampRole(roles: Link[]): boolean {
    return throwAbstractException('isCalampRole');
  }

  /**
   * Determines if the given feature (via id) is temporarily restricted, which means
   * it should never be shown to (all) users.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isRestrictedFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isRestrictedFeature(UTILS);                   // true
   *   const showFeature = Features.isRestrictedFeature(ADMIN_ACCOUNTS);          // false
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isRestrictedFeature(featureId: string): boolean {
    return throwAbstractException('isRestrictedFeature');
  }

  /**
   * Determines if the given feature (via id) is an internal feature, which means it should only be shown to
   * internal (Calamp) roles.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isInternalFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isInternalFeature(MANUFACTURED_DEVICES);    // true
   *   const showFeature = Features.isInternalFeature(ADMIN_ACCOUNTS);          // false
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isInternalFeature(featureId: string): boolean {
    return throwAbstractException('isInternalFeature');
  }

  /**
   * Determines if the given feature (via id) is a device feature.
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const showFeature = Features.isDeviceFeature(MANUFACTURING_INVENTORY); // false
   *   const showFeature = Features.isDeviceFeature(DEVICES);                 // true
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  isDeviceFeature(featureId: string): boolean {
    return throwAbstractException('isDeviceFeature');
  }

  /**
   * Retrieves the statically mapped permissions for the given feature (via id).
   *
   * ```typescript
   *
   *   import Features from './security';
   *
   *   const inventoryFeaturePermissions = Features.getMappedPermissions(MANUFACTURING_INVENTORY);
   *
   *   inventoryFeaturePermissions; // [Entity.manufacturing]
   *   ...
   *
   * ```
   *
   * @returns true if the feature is restricted from all roles, false otherwise.
   */
  getMappedPermissions(featureId: string): GenericObject {
    return throwAbstractException('getMappedPermissions');
  }

  /**
   * @private
   */
  __reset__() {
    Object.keys(this._permittedFeature).forEach(key => {
      this._permittedFeature[key] = undefined;
      delete this._permittedFeature[key];
    });
  }
}

export default ApplicationFeatures;
